import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, ButtonBase, Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem,
    Popover, Slider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { HexColorPicker, RgbaColorPicker } from 'react-colorful';
import FormatAlignLeftIcon   from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon  from '@mui/icons-material/FormatAlignRight';
import OpenInFullIcon        from '@mui/icons-material/OpenInFull';
import CloseIcon             from '@mui/icons-material/Close';
import DragIndicatorIcon     from '@mui/icons-material/DragIndicator';
import TextFieldsIcon        from '@mui/icons-material/TextFields';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, pointerWithin, rectIntersection } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ConfigCard } from '../cards';
import { ModalCardFlow } from '../../_designVariant';
import { SubLabel, PdiRow } from '../cards/_PdiHelpers';

// All product fields the legacy storefront knows how to render under a
// product card. Stored as space-separated tokens per line in
// PRODUCT_DISPLAY_INFO (lines themselves are `;`-separated). The legacy
// admin allowed arbitrary text in between tokens (e.g. "brand (id)" →
// "Braxx (2231)") — our chip UI only handles pure token sequences, so
// freeform templates round-trip as plain space-joined tokens.
const ALL_FIELDS = [
    { key: 'id',          i18nKey: 'cfg_pcard_field_id',          fallback: 'ID' },
    { key: 'brand',       i18nKey: 'cfg_pcard_field_brand',       fallback: 'Brand' },
    { key: 'name',        i18nKey: 'cfg_pcard_field_name',        fallback: 'Name' },
    { key: 'reference',   i18nKey: 'cfg_pcard_field_reference',   fallback: 'Reference' },
    { key: 'season',      i18nKey: 'cfg_pcard_field_season',      fallback: 'Season' },
    { key: 'color',       i18nKey: 'cfg_pcard_field_color',       fallback: 'Color' },
    { key: 'color_brand', i18nKey: 'cfg_pcard_field_color_brand', fallback: 'Color (brand name)' },
    { key: 'description_1', i18nKey: 'cfg_pcard_field_description_1', fallback: 'Description 1' },
    { key: 'description_2', i18nKey: 'cfg_pcard_field_description_2', fallback: 'Description 2' },
    { key: 'description_3', i18nKey: 'cfg_pcard_field_description_3', fallback: 'Description 3' },
    { key: 'description_4', i18nKey: 'cfg_pcard_field_description_4', fallback: 'Description 4' },
    { key: 'description_5', i18nKey: 'cfg_pcard_field_description_5', fallback: 'Description 5' },
    { key: 'category_1', i18nKey: 'cfg_pcard_field_category_1', fallback: 'Category 1' },
    { key: 'category_2', i18nKey: 'cfg_pcard_field_category_2', fallback: 'Category 2' },
    { key: 'category_3', i18nKey: 'cfg_pcard_field_category_3', fallback: 'Category 3' },
    { key: 'category_4', i18nKey: 'cfg_pcard_field_category_4', fallback: 'Category 4' },
    { key: 'category_5', i18nKey: 'cfg_pcard_field_category_5', fallback: 'Category 5' },
    { key: 'category_6', i18nKey: 'cfg_pcard_field_category_6', fallback: 'Category 6' },
    { key: 'category_7', i18nKey: 'cfg_pcard_field_category_7', fallback: 'Category 7' },
    { key: 'detail_1',   i18nKey: 'cfg_pcard_field_detail_1',   fallback: 'Detail 1' },
    { key: 'detail_2',   i18nKey: 'cfg_pcard_field_detail_2',   fallback: 'Detail 2' },
    { key: 'detail_3',   i18nKey: 'cfg_pcard_field_detail_3',   fallback: 'Detail 3' },
    { key: 'detail_4',   i18nKey: 'cfg_pcard_field_detail_4',   fallback: 'Detail 4' },
    { key: 'detail_5',   i18nKey: 'cfg_pcard_field_detail_5',   fallback: 'Detail 5' },
];

// Labels resolve through tr() at render time; English text = fallback.
const ANIMATION_TYPES = [
    { v: 'fadeAlt',  i18nKey: 'cfg_pcard_anim_fade_alt', l: 'Fade to alternative image' },
    { v: 'zoom',     i18nKey: 'cfg_pcard_anim_zoom',     l: 'Zoom in'                   },
    { v: 'shimmer',  i18nKey: 'cfg_pcard_anim_shimmer',  l: 'Shimmer'                   },
    { v: 'none',     i18nKey: 'cfg_pcard_anim_none',     l: 'No animation'              },
];

// Chip helpers. Each chip on a line is one of:
//  - Token chip:  { id, key }   — references a known field; renders coloured
//  - Text  chip:  { id, text }  — literal text the user typed; renders as
//                                  an inline editable input
// On the storefront, the rendered line is the chips space-joined and run
// through the renderer's `\b(known_key)\b` regex replacement, so a text
// chip containing `(id)` still gets `id` swapped in — exactly the legacy
// behaviour. The chip UI just lets the admin compose that template.
let _chipIdCounter = 0;
const _newId = (kind) => `${kind}-${Date.now()}-${++_chipIdCounter}`;
export const tokenChip = (key)  => ({ id: _newId('chip'), key });
export const textChip  = (text) => ({ id: _newId('txt'),  text });

// Default config — also used as the source of truth for the Verkenner
// preview when no parent has overridden anything yet. Each line is a chip
// array. Token chips reference a known field; text chips carry literal text
// (e.g. " - " or "(id)") so the line "brand (id)" round-trips as two chips.
export const DEFAULT_PRODUCT_CARD_CONFIG = {
    productInfo: {
        // Mirror the legacy storefront's compile-time defaults in
        // item-product.component.ts: displayInfo_0='brand', _1='reference',
        // _2='name'. Live PRODUCT_DISPLAY_INFO loaded from the DB overrides
        // these as soon as the screen mounts.
        line1: [tokenChip('brand')],
        line2: [tokenChip('reference')],
        line3: [tokenChip('name')],
        showDivider: true,
        align:       'center',
    },
    image: {
        useAlternativeAsMain: false,
        fillsCard:            true,
        aspectWidth:          3,
        aspectHeight:         5,
        cornerRounding:       5,
        animateOnHover:       true,
        animationType:        'fadeAlt',
    },
    sizePreview: {
        enabled:      true,
        // Default matches the legacy install (PRODUCT_HOVER_MAX_SIZES = '3,5',
        // index [0] = "lengtematen" / number of sizes shown in the hover badge).
        maxShown:     3,
        position:     'bottom',
        badgeBg:      '#ffffff',
        badgeText:    '#000000',
        textSize:     15,
        showBorder:   false,
    },
};

// When a parent passes value/onChange, the modal becomes "controlled" and
// every edit flows up. When neither is passed, it falls back to local state
// (used as a self-contained viewer in storybook-style contexts).
const ProductCardModal = ({ value, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const [localConfig, setLocalConfig] = useState(DEFAULT_PRODUCT_CARD_CONFIG);
    const controlled = value !== undefined && typeof onChange === 'function';
    const config    = controlled ? value      : localConfig;
    // Forward the updater itself (onChange is the parent's setState): resolving
    // it here against the render-time `config` dropped concurrent/batched
    // edits — same fix as the carousel/image-grid modals. useCallback so the
    // chip handlers below can be stable too (F14).
    const setConfig = useCallback(
        (updater) => (controlled ? onChange(updater) : setLocalConfig(updater)),
        [controlled, onChange],
    );

    const setSection = (k) => (patch) => setConfig(p => ({ ...p, [k]: { ...(p[k] || {}), ...patch } }));
    const setProductInfo = setSection('productInfo');
    const setImage       = setSection('image');
    const setSizePreview = setSection('sizePreview');

    // Add a NEW chip instance for `fieldKey` to the end of `lineId` (used
    // by the "click to add" available row + by the drop-from-available
    // drag handler). Same field can live on multiple lines simultaneously.
    const addChipToLine = useCallback((fieldKey, lineId) => {
        setConfig(p => ({ ...p, productInfo: { ...p.productInfo, [lineId]: [...p.productInfo[lineId], tokenChip(fieldKey)] } }));
    }, [setConfig]);

    // Append an empty text chip to the line so the user can type literal
    // text (separators, parens, etc.) between tokens. Always added at the
    // end; the user reorders via the drag handle like any other chip.
    const addTextChipToLine = useCallback((lineId) => {
        setConfig(p => ({ ...p, productInfo: { ...p.productInfo, [lineId]: [...p.productInfo[lineId], textChip('')] } }));
    }, [setConfig]);

    // Update the text of a specific text chip (called from each chip's own
    // inline TextField onChange).
    const updateTextChip = useCallback((chipId, newText) => {
        setConfig(p => {
            const next = { ...p.productInfo };
            let hit = false;
            for (const lineId of ['line1', 'line2', 'line3']) {
                if (next[lineId].some(c => c.id === chipId)) {
                    next[lineId] = next[lineId].map(c => (c.id === chipId ? { ...c, text: newText } : c));
                    hit = true;
                }
            }
            return hit ? { ...p, productInfo: next } : p;
        });
    }, [setConfig]);

    // Remove one specific chip instance (by id) from whichever line it's on.
    const removeChipById = useCallback((chipId) => {
        setConfig(p => ({ ...p, productInfo: {
            ...p.productInfo,
            line1: p.productInfo.line1.filter(c => c.id !== chipId),
            line2: p.productInfo.line2.filter(c => c.id !== chipId),
            line3: p.productInfo.line3.filter(c => c.id !== chipId),
        } }));
    }, [setConfig]);

    // Look up which line currently holds a given chip id.
    const findLineOfChip = (chipId) => {
        if (config.productInfo.line1.some(c => c.id === chipId)) return 'line1';
        if (config.productInfo.line2.some(c => c.id === chipId)) return 'line2';
        if (config.productInfo.line3.some(c => c.id === chipId)) return 'line3';
        return null;
    };

    // ── Drag-and-drop handler ──
    // Active id can be either a chip instance id ('chip-…') already on a
    // line, OR a synthetic 'avail-<key>' id from the available pool. Over
    // id is either another chip (= drop on/before that chip) or a line
    // container ('drop-line1'/'drop-line2'/'drop-line3') = empty drop.
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;

        const activeIsAvail = String(active.id).startsWith('avail-');
        const overIsLineBox = String(over.id).startsWith('drop-line');

        // Resolve target line id (line1/line2/line3).
        let targetLine = null;
        if (overIsLineBox) {
            targetLine = String(over.id).replace('drop-', '');
        } else {
            targetLine = findLineOfChip(over.id);
        }
        if (!targetLine) return;

        if (activeIsAvail) {
            // Clone an available field onto the target line — at the end if
            // dropped on the line container, before the over-chip otherwise.
            const fieldKey = String(active.id).replace('avail-', '');
            const newChip  = tokenChip(fieldKey);
            const targetList = config.productInfo[targetLine];
            const insertAt = overIsLineBox ? targetList.length
                : Math.max(0, targetList.findIndex(c => c.id === over.id));
            const next = [...targetList];
            next.splice(insertAt, 0, newChip);
            setProductInfo({ [targetLine]: next });
            return;
        }

        // Move an existing chip — reorder within the same line OR move
        // across lines.
        const sourceLine = findLineOfChip(active.id);
        if (!sourceLine) return;

        if (sourceLine === targetLine) {
            const list = config.productInfo[sourceLine];
            const oldIdx = list.findIndex(c => c.id === active.id);
            const newIdx = overIsLineBox ? list.length - 1
                : list.findIndex(c => c.id === over.id);
            if (oldIdx === newIdx || oldIdx < 0 || newIdx < 0) return;
            setProductInfo({ [sourceLine]: arrayMove(list, oldIdx, newIdx) });
        } else {
            const sourceList = config.productInfo[sourceLine];
            const targetList = config.productInfo[targetLine];
            const movedChip = sourceList.find(c => c.id === active.id);
            if (!movedChip) return;
            const newSource = sourceList.filter(c => c.id !== active.id);
            const insertAt  = overIsLineBox ? targetList.length
                : Math.max(0, targetList.findIndex(c => c.id === over.id));
            const newTarget = [...targetList];
            newTarget.splice(insertAt, 0, movedChip);
            setProductInfo({ [sourceLine]: newSource, [targetLine]: newTarget });
        }
    };


    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
    );

    // closestCenter (the dnd-kit default) treats an empty droppable as a
    // small point: when line3 is empty, dropping near it would bias toward
    // the bigger line2 because that line's centre stays closer. pointer-
    // within picks the line the cursor is literally inside, so empty line3
    // works as expected. rectIntersection is the fallback for the case where
    // the cursor leaves all droppables mid-drag (e.g. fast moves).
    const collisionDetection = (args) => {
        const pointerIntersections = pointerWithin(args);
        if (pointerIntersections.length > 0) return pointerIntersections;
        return rectIntersection(args);
    };

    return (
        // Wide modal → flow the cards into two balanced (masonry) columns on
        // large screens so the canvas is actually used; single column otherwise.
        <ModalCardFlow>
            {/* Product information — three lines + divider + align */}
            <ConfigCard
                anchor="cfg-card-pcard-info"
                title={tr('cfg_pcard_info_title', 'Product information')}
                description={tr('cfg_pcard_info_desc', 'Configure the three text lines shown under each product card. Drag chips between lines to reorder, or click an available field to add it.')}
                theme={theme}
            >
                <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
                    <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                        <LineRow
                            lineId="line1"
                            label={tr('cfg_pcard_line_1', 'LINE 1')}
                            chips={config.productInfo.line1}
                            onRemove={removeChipById}
                            onAddText={() => addTextChipToLine('line1')}
                            onUpdateText={updateTextChip}
                            tr={tr}
                        />
                        {/* Divider visual between line 1 and line 2 — matches the
                            actual storefront divider style (`1px solid lightgrey`). */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, color: 'text.disabled' }}>
                            <Box sx={{ flex: 1, height: 0, borderTop: '1px solid lightgrey' }} />
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                {tr('cfg_pcard_divider', 'divider')}
                            </Typography>
                            <Box sx={{ flex: 1, height: 0, borderTop: '1px solid lightgrey' }} />
                        </Box>
                        <LineRow
                            lineId="line2"
                            label={tr('cfg_pcard_line_2', 'LINE 2')}
                            chips={config.productInfo.line2}
                            onRemove={removeChipById}
                            onAddText={() => addTextChipToLine('line2')}
                            onUpdateText={updateTextChip}
                            tr={tr}
                        />
                        <Box sx={{ height: 8 }} />
                        <LineRow
                            lineId="line3"
                            label={tr('cfg_pcard_line_3', 'LINE 3')}
                            chips={config.productInfo.line3}
                            onRemove={removeChipById}
                            onAddText={() => addTextChipToLine('line3')}
                            onUpdateText={updateTextChip}
                            emptyText={tr('cfg_pcard_drop_here', 'Drag fields here')}
                            tr={tr}
                        />

                        {/* Available pool — always shows the full set so the
                            same field can be dragged onto multiple lines. */}
                        <Box sx={{ mt: 1.5 }}>
                            <SubLabel>{tr('cfg_pcard_available', 'Available fields (click or drag to add)')}</SubLabel>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {ALL_FIELDS.map(f => (
                                    <DraggableAvailableChip
                                        key={f.key}
                                        fieldKey={f.key}
                                        label={tr(f.i18nKey, f.fallback)}
                                        onAdd={() => addChipToLine(f.key, 'line1')}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </DndContext>

                <PdiRow
                    label={tr('cfg_pcard_show_divider', 'Show divider between line 1 and line 2')}
                    help={tr('cfg_pcard_show_divider_help', 'A thin separator line visually splits the first line from the rest of the product information')}
                    control={<Switch color="success" size="small" checked={!!config.productInfo.showDivider} onChange={(e) => setProductInfo({ showDivider: e.target.checked })} />}
                />
                <PdiRow
                    label={tr('cfg_pcard_align', 'Align product information')}
                    help={tr('cfg_pcard_align_help', 'How the title, price, and other text underneath the image are aligned')}
                    align="top"
                    control={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[
                                { v: 'left',   l: tr('cfg_pcard_align_left',   'Left'),   icon: <FormatAlignLeftIcon /> },
                                { v: 'center', l: tr('cfg_pcard_align_center', 'Center'), icon: <FormatAlignCenterIcon /> },
                                { v: 'right',  l: tr('cfg_pcard_align_right',  'Right'),  icon: <FormatAlignRightIcon /> },
                            ].map(o => {
                                const sel = config.productInfo.align === o.v;
                                return (
                                    <ButtonBase
                                        key={o.v}
                                        onClick={() => setProductInfo({ align: o.v })}
                                        sx={{
                                            flexDirection: 'column', gap: 0.25,
                                            p: 1, minWidth: 80,
                                            borderRadius: 1.5,
                                            border: '1.5px solid',
                                            borderColor: sel ? 'primary.main' : 'divider',
                                            backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                                            color: sel ? 'primary.main' : 'text.secondary',
                                        }}
                                    >
                                        {o.icon}
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{o.l}</Typography>
                                    </ButtonBase>
                                );
                            })}
                        </Box>
                    }
                />
            </ConfigCard>

            {/* Product image */}
            <ConfigCard
                anchor="cfg-card-pcard-image"
                title={tr('cfg_pcard_image_title', 'Product image')}
                description={tr('cfg_pcard_image_desc', 'How the product photo looks in each card.')}
                theme={theme}
            >
                <PdiRow
                    label={tr('cfg_pcard_use_alt_main', 'Use the alternative image as the main one')}
                    help={tr('cfg_pcard_use_alt_main_help', 'Show the secondary product photo as the primary image in listings, instead of the default')}
                    control={<Switch color="success" size="small" checked={!!config.image.useAlternativeAsMain} onChange={(e) => setImage({ useAlternativeAsMain: e.target.checked })} />}
                />
                <PdiRow
                    label={tr('cfg_pcard_fills_card', 'Image fills the entire card')}
                    help={tr('cfg_pcard_fills_card_help', 'Stretches the image edge-to-edge, with no padding around it. The image is scaled (not resized) to fit the aspect ratio below.')}
                    control={<Switch color="success" size="small" checked={!!config.image.fillsCard} onChange={(e) => setImage({ fillsCard: e.target.checked })} />}
                />

                <PdiRow
                    label={tr('cfg_pcard_aspect', 'Image aspect ratio')}
                    /* Two-sentence help — manually split so the second sentence
                       wraps to a new line. Keeps the help column narrow enough
                       that the right-side controls have room for all three
                       preset buttons on a single line. */
                    help={(
                        <>
                            {tr('cfg_pcard_aspect_help_p1', 'Width to height ratio for product images.')}
                            <br />
                            {tr('cfg_pcard_aspect_help_p2', 'The preview updates as you drag the sliders.')}
                        </>
                    )}
                    align="top"
                    control={
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <AspectPreview width={config.image.aspectWidth} height={config.image.aspectHeight} />
                            <Box sx={{ width: 320 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 56 }}>WIDTH</Typography>
                                    <Slider size="small" min={1} max={10} step={1}
                                        value={config.image.aspectWidth}
                                        onChange={(_e, v) => setImage({ aspectWidth: v })}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />
                                    <Typography variant="body2" sx={{ minWidth: 16, textAlign: 'right', fontWeight: 700 }}>{config.image.aspectWidth}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 56 }}>HEIGHT</Typography>
                                    <Slider size="small" min={1} max={10} step={1}
                                        value={config.image.aspectHeight}
                                        onChange={(_e, v) => setImage({ aspectHeight: v })}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />
                                    <Typography variant="body2" sx={{ minWidth: 16, textAlign: 'right', fontWeight: 700 }}>{config.image.aspectHeight}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {tr('cfg_pcard_ratio', 'Ratio')}: <Box component="strong">{config.image.aspectWidth}:{config.image.aspectHeight}</Box>
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <PresetBtn onClick={() => setImage({ aspectWidth: 1, aspectHeight: 1 })}>{tr('cfg_pcard_square',    'Square')}</PresetBtn>
                                    <PresetBtn onClick={() => setImage({ aspectWidth: 3, aspectHeight: 4 })}>{tr('cfg_pcard_portrait',  'Portrait')}</PresetBtn>
                                    <PresetBtn onClick={() => setImage({ aspectWidth: 16, aspectHeight: 9 })}>{tr('cfg_pcard_landscape', 'Landscape')}</PresetBtn>
                                </Box>
                            </Box>
                        </Box>
                    }
                />
                <PdiRow
                    label={tr('cfg_pcard_corner_rounding', 'Image corner rounding')}
                    help={tr('cfg_pcard_corner_rounding_help', 'How rounded the corners of the product image are')}
                    control={
                        <TextField size="small" type="number"
                            value={config.image.cornerRounding}
                            onChange={(e) => setImage({ cornerRounding: Math.max(0, parseInt(e.target.value) || 0) })}
                            InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_pcard_pixels', 'pixels')}</InputAdornment> }}
                            sx={{ width: 140 }}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_pcard_animate', 'Animate the image on hover')}
                    help={tr('cfg_pcard_animate_help', 'Adds a small effect when a visitor hovers over a product')}
                    control={<Switch color="success" size="small" checked={!!config.image.animateOnHover} onChange={(e) => setImage({ animateOnHover: e.target.checked })} />}
                />
                <Box sx={{ pl: 2.5, borderLeft: '2px solid', borderColor: 'divider', opacity: config.image.animateOnHover ? 1 : 0.5, pointerEvents: config.image.animateOnHover ? 'auto' : 'none' }}>
                    <PdiRow
                        label={tr('cfg_pcard_animation_type', 'Animation type')}
                        control={
                            <TextField select size="small"
                                value={config.image.animationType}
                                onChange={(e) => setImage({ animationType: e.target.value })}
                                sx={{ minWidth: 240 }}
                            >
                                {ANIMATION_TYPES.map(o => <MenuItem key={o.v} value={o.v}>{tr(o.i18nKey, o.l)}</MenuItem>)}
                            </TextField>
                        }
                    />
                </Box>
            </ConfigCard>

            {/* Size preview on hover */}
            <ConfigCard
                anchor="cfg-card-pcard-size"
                title={tr('cfg_pcard_size_title', 'Size preview on hover')}
                description={tr('cfg_pcard_size_desc', 'Show available sizes when a visitor hovers over a product card. Useful for clothing and shoes.')}
                theme={theme}
            >
                <PdiRow
                    label={tr('cfg_pcard_size_show', 'Show size preview on hover')}
                    help={tr('cfg_pcard_size_show_help', 'Sizes appear in a small badge over the product image')}
                    control={<Switch color="success" size="small" checked={!!config.sizePreview.enabled} onChange={(e) => setSizePreview({ enabled: e.target.checked })} />}
                />
                <Box sx={{ opacity: config.sizePreview.enabled ? 1 : 0.5, pointerEvents: config.sizePreview.enabled ? 'auto' : 'none' }}>
                    <Stack spacing={1.25}>
                        <PdiRow
                            indent
                            label={tr('cfg_pcard_size_max', 'Maximum sizes shown')}
                            help={tr('cfg_pcard_size_max_help', 'If the product has more sizes, a "+N" indicator shows how many extra are available')}
                            control={
                                <TextField size="small" type="number"
                                    value={config.sizePreview.maxShown}
                                    onChange={(e) => setSizePreview({ maxShown: Math.max(0, parseInt(e.target.value) || 0) })}
                                    InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_pcard_sizes', 'sizes')}</InputAdornment> }}
                                    sx={{ width: 140 }}
                                />
                            }
                        />
                        <PdiRow
                            indent
                            label={tr('cfg_pcard_badge_position', 'Badge position')}
                            help={tr('cfg_pcard_badge_position_help', 'Where the size badge appears over the product image')}
                            align="top"
                            control={
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {[
                                        { v: 'top',    l: tr('cfg_pcard_pos_top',    'Top') },
                                        { v: 'bottom', l: tr('cfg_pcard_pos_bottom', 'Bottom') },
                                    ].map(o => {
                                        const sel = config.sizePreview.position === o.v;
                                        return (
                                            <ButtonBase
                                                key={o.v}
                                                onClick={() => setSizePreview({ position: o.v })}
                                                sx={{
                                                    flexDirection: 'column', gap: 0.5,
                                                    p: 1.25, minWidth: 92,
                                                    borderRadius: 1.5,
                                                    border: '1.5px solid',
                                                    borderColor: sel ? 'primary.main' : 'divider',
                                                    backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                                                    color: sel ? 'primary.main' : 'text.secondary',
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 38, height: 26, border: '1px solid', borderColor: 'inherit', borderRadius: 0.5,
                                                    position: 'relative', backgroundColor: '#fff',
                                                }}>
                                                    <Box sx={{
                                                        position: 'absolute', left: 4, right: 4,
                                                        [o.v === 'top' ? 'top' : 'bottom']: 4,
                                                        height: 4, backgroundColor: 'currentColor', borderRadius: 0.25,
                                                    }} />
                                                </Box>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{o.l}</Typography>
                                            </ButtonBase>
                                        );
                                    })}
                                </Box>
                            }
                        />
                        <PdiRow
                            indent
                            label={tr('cfg_pcard_badge_style', 'Badge style')}
                            help={tr('cfg_pcard_badge_style_help', 'Background, text color, and size of the badge')}
                            align="top"
                            control={
                                <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', display: 'grid', gridTemplateColumns: 'auto auto', gap: 2 }}>
                                    <Box>
                                        <SubLabel noLine>{tr('cfg_pcard_badge_bg', 'BACKGROUND')}</SubLabel>
                                        <ColorSwatch alpha value={config.sizePreview.badgeBg} onChange={(v) => setSizePreview({ badgeBg: v })} />
                                    </Box>
                                    <Box>
                                        <SubLabel noLine>{tr('cfg_pcard_badge_text', 'TEXT COLOR')}</SubLabel>
                                        <ColorSwatch value={config.sizePreview.badgeText} onChange={(v) => setSizePreview({ badgeText: v })} />
                                    </Box>
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <SubLabel noLine>{tr('cfg_pcard_badge_text_size', 'TEXT SIZE')}</SubLabel>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <TextField size="small" type="number"
                                                value={config.sizePreview.textSize}
                                                // Don't clamp on every keystroke — typing "1" with intent of
                                                // "15" otherwise snaps to the min (8) mid-keystroke. We accept
                                                // any partial value while editing and clamp on blur.
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === '') {
                                                        setSizePreview({ textSize: '' });
                                                    } else {
                                                        const n = parseInt(raw, 10);
                                                        if (Number.isFinite(n)) setSizePreview({ textSize: n });
                                                    }
                                                }}
                                                onBlur={() => {
                                                    const n = parseInt(config.sizePreview.textSize, 10);
                                                    const safe = Number.isFinite(n) ? Math.max(8, Math.min(200, n)) : 15;
                                                    if (safe !== config.sizePreview.textSize) {
                                                        setSizePreview({ textSize: safe });
                                                    }
                                                }}
                                                inputProps={{ min: 8, max: 200, step: 1 }}
                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                sx={{ width: 110, backgroundColor: '#fff' }}
                                            />
                                            <FormControlLabel
                                                control={<Checkbox size="small" checked={!!config.sizePreview.showBorder} onChange={(e) => setSizePreview({ showBorder: e.target.checked })} />}
                                                label={<Typography variant="body2">{tr('cfg_pcard_badge_border', 'Show border around each size')}</Typography>}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            }
                        />
                    </Stack>
                </Box>
            </ConfigCard>
        </ModalCardFlow>
    );
};
ProductCardModal.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, t: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

// One LINE row — left label + chips + "+ Text" button on the right. Acts
// as a droppable zone (id = `drop-line1` / `drop-line2` / `drop-line3`)
// and wraps its chips in a horizontal SortableContext so within-line
// reordering works.
// F14: O(1) veld-lookup + memoised chip components. Typing in one text chip
// re-rendered every chip on all three lines plus the 24 draggable available
// chips (each re-running useSortable/useDraggable).
const FIELD_BY_KEY = new Map(ALL_FIELDS.map(f => [f.key, f]));

const LineRow = React.memo(({ lineId, label, chips, onRemove, onAddText, onUpdateText, emptyText, tr }) => {
    const { setNodeRef, isOver } = useDroppable({ id: `drop-${lineId}` });
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 56, flexShrink: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                    {label}
                </Typography>
            </Box>
            <Box
                ref={setNodeRef}
                sx={{
                    flex: 1,
                    backgroundColor: isOver ? 'rgba(25,118,210,0.06)' : '#fff',
                    border: '1px solid',
                    borderColor: isOver ? 'primary.main' : 'divider',
                    borderRadius: 1, px: 1, py: 0.75,
                    display: 'flex', flexWrap: 'wrap', gap: 0.5,
                    minHeight: 36,
                    alignItems: 'center',
                    transition: 'background-color 0.12s, border-color 0.12s',
                }}
            >
                <SortableContext items={chips.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                    {chips.length === 0 ? (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', px: 1 }}>
                            {emptyText || tr('cfg_pcard_drop_here', 'Drag fields here')}
                        </Typography>
                    ) : chips.map(c => (
                        <SortableLineChip key={c.id} chip={c} onRemove={onRemove} onUpdateText={onUpdateText} tr={tr} />
                    ))}
                </SortableContext>
            </Box>
            {/* "+ Text" — drops an empty text chip at the end of this line
                so the user can type literal text (separators, parens, etc.)
                between tokens. */}
            <ButtonBase
                onClick={onAddText}
                title={tr('cfg_pcard_add_text', 'Add free text')}
                sx={{
                    flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                    px: 1, py: 0.4,
                    borderRadius: 999,
                    border: '1px dashed',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                }}
            >
                <TextFieldsIcon sx={{ fontSize: 13 }} />
                {tr('cfg_pcard_add_text_short', '+ Text')}
            </ButtonBase>
        </Box>
    );
});
LineRow.propTypes = { lineId: PropTypes.string, label: PropTypes.string, chips: PropTypes.array, onRemove: PropTypes.func, onAddText: PropTypes.func, onUpdateText: PropTypes.func, emptyText: PropTypes.string, tr: PropTypes.func };

// A single sortable chip on one of the lines. The drag-handle is the whole
// chip body; the × button uses pointerDown stopPropagation so removing
// doesn't accidentally start a drag.
const SortableLineChip = React.memo(({ chip: c, onRemove, onUpdateText, tr }) => {
    const isText = c.text !== undefined;
    const meta   = isText ? null : FIELD_BY_KEY.get(c.key);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };
    if (!isText && !meta) return null;

    // Text chip: neutral background, inline editable input. Only the drag-
    // handle on the left starts a drag; the input itself is keyboard-
    // friendly and ignores drag listeners.
    if (isText) {
        return (
            <Box
                ref={setNodeRef}
                style={style}
                {...attributes}
                sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.25,
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: 999,
                    pl: 0.25, pr: 0.5, py: 0.25,
                    userSelect: 'none',
                }}
            >
                <Box {...listeners} sx={{ display: 'inline-flex', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
                    <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </Box>
                <TextField
                    size="small"
                    variant="standard"
                    value={c.text}
                    onChange={(e) => onUpdateText && onUpdateText(c.id, e.target.value)}
                    placeholder={tr('cfg_pcard_text_placeholder', 'free text')}
                    onPointerDown={(e) => e.stopPropagation()}
                    InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', px: 0.5 } }}
                    sx={{ minWidth: 40, '& input': { padding: 0, width: `${Math.max(40, (c.text || '').length * 7 + 12)}px` } }}
                />
                <IconButton
                    size="small"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onRemove(c.id); }}
                    sx={{ width: 18, height: 18, color: 'text.disabled' }}
                >
                    <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
            </Box>
        );
    }

    // Token chip: locked label, draggable everywhere, × to remove.
    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
                backgroundColor: 'rgba(25,118,210,0.06)',
                border: '1px solid', borderColor: 'primary.light',
                borderRadius: 999,
                pl: 0.75, pr: 0.5, py: 0.25,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                userSelect: 'none',
            }}
        >
            <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.25 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', px: 0.25 }}>
                {tr(meta.i18nKey, meta.fallback)}
            </Typography>
            <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onRemove(c.id); }}
                sx={{ width: 18, height: 18, color: 'text.disabled' }}
            >
                <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
        </Box>
    );
});
SortableLineChip.propTypes = { chip: PropTypes.object, onRemove: PropTypes.func, onUpdateText: PropTypes.func, tr: PropTypes.func };

// An "available" chip — draggable into a line (id `avail-<key>`) and also
// click-to-add to line 1 as a quick path.
const DraggableAvailableChip = React.memo(({ fieldKey, label, onAdd }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `avail-${fieldKey}` });
    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                border: '1px solid', borderColor: 'divider',
                borderRadius: 999,
                backgroundColor: '#fff',
                px: 1.25, py: 0.5,
                color: 'text.primary',
                cursor: 'grab',
                userSelect: 'none',
                transition: 'all 0.15s',
                '&:hover':  { borderColor: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)', color: 'primary.main' },
                '&:active': { cursor: 'grabbing' },
            }}
            {...attributes}
            {...listeners}
            onClick={(e) => {
                // Tiny pointer-distance threshold means a clean click still
                // fires onClick — translate it into a "click to add" action
                // that drops the chip onto line 1.
                if (!isDragging) onAdd();
                e.preventDefault();
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                <Box component="span" sx={{ fontSize: '0.85em' }}>+</Box>
                {label}
            </Typography>
        </Box>
    );
});
DraggableAvailableChip.propTypes = { fieldKey: PropTypes.string, label: PropTypes.node, onAdd: PropTypes.func };

// Visual aspect ratio preview — small box shaped to width:height.
const AspectPreview = ({ width, height }) => {
    // Cap at 80px so the preview doesn't grow unmanageably large.
    const ratio = Math.max(0.1, height / Math.max(1, width));
    const boxW = ratio > 1 ? Math.round(80 / ratio) : 80;
    const boxH = ratio > 1 ? 80 : Math.round(80 * ratio);
    return (
        <Box sx={{
            width: 100, minHeight: 100,
            backgroundColor: 'rgba(0,0,0,0.04)',
            border: '1px solid', borderColor: 'divider',
            borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            p: 1,
        }}>
            <Box sx={{
                width: boxW, height: boxH,
                backgroundColor: '#fff',
                border: '1.5px solid', borderColor: 'primary.main',
                borderRadius: 0.75,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'primary.main',
            }}>
                <OpenInFullIcon fontSize="small" />
            </Box>
        </Box>
    );
};
AspectPreview.propTypes = { width: PropTypes.number, height: PropTypes.number };

const PresetBtn = ({ children, onClick }) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            px: 1.25, py: 0.4, borderRadius: 0.75,
            border: '1px solid', borderColor: 'divider',
            backgroundColor: '#fff',
            color: 'text.secondary',
            fontSize: '0.7rem', fontWeight: 600,
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
        }}
    >{children}</ButtonBase>
);
PresetBtn.propTypes = { children: PropTypes.node, onClick: PropTypes.func };

// Parse any color string the legacy admin might have stored. Returns RGB
// (0-255) plus alpha (0-1). Falls back to opaque white for unparseable input.
const parseColor = (raw) => {
    if (!raw) return { r: 255, g: 255, b: 255, a: 1 };
    const str = String(raw).trim();
    let m;
    if ((m = str.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/i))) {
        return { r: +m[1], g: +m[2], b: +m[3], a: m[4] != null ? Math.max(0, Math.min(1, +m[4])) : 1 };
    }
    if ((m = str.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i))) {
        const h = m[1];
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
            a: m[2] ? parseInt(m[2], 16) / 255 : 1,
        };
    }
    if ((m = str.match(/^#([0-9a-f]{3})$/i))) {
        const h = m[1];
        return {
            r: parseInt(h[0] + h[0], 16),
            g: parseInt(h[1] + h[1], 16),
            b: parseInt(h[2] + h[2], 16),
            a: 1,
        };
    }
    return { r: 255, g: 255, b: 255, a: 1 };
};
// Hex output for the native <input type="color"> (which doesn't accept rgba).
const rgbToHex = ({ r, g, b }) =>
    '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
// Final value emitted back to the parent: rgba(…) when alpha < 1, hex when
// fully opaque. Matches the legacy admin's storage convention (it always
// wrote rgba for the size-overlay BGC/FGC, but other settings stored hex).
const toColorString = ({ r, g, b, a }) =>
    a >= 0.999
        ? rgbToHex({ r, g, b })
        : `rgba(${r}, ${g}, ${b}, ${Math.round(a * 1000) / 1000})`;

// Tiny CSS checker pattern so the swatch shows transparency at a glance.
const CHECKER_BG = 'repeating-conic-gradient(#e7e7e7 0deg 90deg, #f6f6f6 90deg 180deg)';
const CHECKER_BG_SIZE = '8px 8px';

const ColorSwatch = ({ value, onChange, alpha = false }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const close = () => setAnchorEl(null);
    const parsed = parseColor(value);

    const emit = (next) => onChange(toColorString(alpha ? next : { ...next, a: 1 }));

    return (
        <>
            <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    position: 'relative', display: 'block',
                    width: 36, height: 36, borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.18)',
                    cursor: 'pointer',
                    background: alpha ? CHECKER_BG : 'transparent',
                    backgroundSize: alpha ? CHECKER_BG_SIZE : undefined,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    position: 'absolute', inset: 0,
                    backgroundColor: alpha ? toColorString(parsed) : rgbToHex(parsed),
                }} />
            </Box>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { p: 1.5 } } }}
            >
                {/* react-colorful renders saturation/value square + hue slider +
                    (when Rgba variant) alpha slider, all in one panel — no
                    OS-level dialog, no second click. Hex readout below for
                    quick verification. */}
                {alpha ? (
                    <RgbaColorPicker
                        color={parsed}
                        onChange={(c) => emit({ r: c.r, g: c.g, b: c.b, a: c.a })}
                    />
                ) : (
                    <HexColorPicker
                        color={rgbToHex(parsed)}
                        onChange={(hex) => {
                            const { r, g, b } = parseColor(hex);
                            emit({ r, g, b, a: 1 });
                        }}
                    />
                )}
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        {alpha ? toColorString(parsed) : rgbToHex(parsed)}
                    </Typography>
                </Box>
            </Popover>
        </>
    );
};
ColorSwatch.propTypes = { value: PropTypes.string, onChange: PropTypes.func, alpha: PropTypes.bool };

export default ProductCardModal;
